import { existsSync, readdirSync, readFileSync, statSync } from 'fs';
import { dirname, join, resolve, sep } from 'path';
import { parseYaml } from './yaml.js';
import type { BaseMeta } from './types.js';

// Single namespaced workspace folder per CLI so rekor and wayai coexist in one user repo:
//   rekor-ws/rekor.yaml          (repo defaults)
//   rekor-ws/bases/<db>/     (one folder per preview database)
export const WS_DIRNAME = 'rekor-ws';
export const BASES_SUBDIR = 'bases';
export const REPO_CONFIG_FILE = 'rekor.yaml';
export const BASE_META_FILE = 'base.yaml';

/** Walk up from `start` to the git root (the dir containing `.git`, a dir or a worktree file). */
export function findGitRoot(start: string = process.cwd()): string | null {
  let dir = resolve(start);
  for (;;) {
    if (existsSync(join(dir, '.git'))) return dir;
    const parent = dirname(dir);
    if (parent === dir) return null;
    dir = parent;
  }
}

export interface Workspace {
  gitRoot: string;
  wsDir: string;
  databasesDir: string;
}

/** Locate the `rekor-ws` workspace under the git root. `wsDir` may not exist yet (pre-init). */
export function detectWorkspace(start: string = process.cwd()): Workspace | null {
  const gitRoot = findGitRoot(start);
  if (!gitRoot) return null;
  const wsDir = join(gitRoot, WS_DIRNAME);
  return { gitRoot, wsDir, databasesDir: join(wsDir, BASES_SUBDIR) };
}

export interface BaseFolder {
  folder: string;
  meta: BaseMeta;
}

export function readMeta(folder: string): BaseMeta | null {
  const metaPath = join(folder, BASE_META_FILE);
  if (!existsSync(metaPath)) return null;
  try {
    return (parseYaml(readFileSync(metaPath, 'utf-8')) as BaseMeta | null) ?? null;
  } catch {
    return null;
  }
}

/** Every `bases/<x>/` folder that carries a `base.yaml`. */
export function listBaseFolders(ws: Workspace): BaseFolder[] {
  if (!existsSync(ws.databasesDir)) return [];
  const out: BaseFolder[] = [];
  for (const entry of readdirSync(ws.databasesDir)) {
    const folder = join(ws.databasesDir, entry);
    if (!statSync(folder).isDirectory()) continue;
    const meta = readMeta(folder);
    if (meta) out.push({ folder, meta });
  }
  return out;
}

/** Folders for previews already created on the server (have a `base_id`). */
export function scanExisting(ws: Workspace): BaseFolder[] {
  return listBaseFolders(ws).filter((d) => !!d.meta.base_id);
}

/** Folders for previews not yet created (have an `origin_base_id` but no `base_id`). */
export function scanNew(ws: Workspace): BaseFolder[] {
  return listBaseFolders(ws).filter((d) => !d.meta.base_id && !!d.meta.origin_base_id);
}

/**
 * True when a folder's `base.yaml` marks it a read-only production mirror — written alongside a
 * preview by `rekor pull` so the live production config is browsable, never an edit target. Excluded
 * from auto-resolution and refused by `push`.
 */
export function isProductionFolder(meta: BaseMeta): boolean {
  return meta.environment === 'production';
}

/** Find the folder enclosing `cwd` (so a bare `pull`/`push` run inside a db folder targets it). */
export function findEnclosingFolder(ws: Workspace, cwd: string = process.cwd()): BaseFolder | null {
  let dir = resolve(cwd);
  const root = resolve(ws.databasesDir);
  while (dir.startsWith(root + sep) || dir === root) {
    const meta = readMeta(dir);
    if (meta) return { folder: dir, meta };
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return null;
}

/**
 * Resolve a CLI selector to an absolute database-folder path (which may not exist yet — pull
 * creates it on first run). A selector containing a path separator or pointing at an existing
 * path is treated as a path; otherwise it names a folder under `bases/`.
 */
export function folderForSelector(ws: Workspace, selector: string): string {
  if (selector.includes('/') || selector.includes(sep) || existsSync(selector)) {
    return resolve(selector);
  }
  return join(ws.databasesDir, selector);
}
